import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip
} from 'recharts';
import {
    ConvertData, CustomTooltip, getGroupedSeriesByGroupId,
    getGroupedSeriesByGroupment, GetXLSXData, getGroupedSeriesByUUID
} from './ChartsV2Functions';
import "./ChartV2.css";
import uuid from "react-uuid";
import styled from "styled-components";
import HelpIcon from '@mui/icons-material/Help';
import SweetAlert from "react-bootstrap-sweetalert";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme) => ({
    icon: {
        left: "5px",
        top: "30px",
        width: "16px",
        height: "16px",
        verticalAlign: "middle",
        cursor: "pointer",
        color: "#605F70",
        marginLeft: "1px",
        marginTop: "-2px",
        position: "sticky",
    },
}));


const Data = styled.div`
font-size: 14px;
font-weight: 600;
opacity: 1;
`;
const DataResult = styled.div`
  padding: 0.25rem 0.25rem;
  font-size: 14px;
  font-weight: 400;
  opacity: 1;
`;
const DataResultGood = styled.div`
  padding: 0.25rem 0.25rem;
  font-size: 14px;
  opacity: 1;
  color: #91C73C;
  font-weight: bold;
`;
const DataResultBad = styled.div`
  padding: 0.25rem 0.25rem;
  font-size: 14px;
  opacity: 1;
  color: #C53F3F;
  font-weight: bold;
`;
const DataContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 0.1rem 1rem;
  flex-wrap: wrap;
`;
const HelpLine = styled.p`
  text-align : left;
`;

const RADIAN = Math.PI / 180;

const needle = (value, data, cx, cy, iR, oR, color, unit, currentValue) => {
    //console.log(cx);
    let total = 0;
    data.forEach((v) => {
        total += v.value;
    });
    const ang = 180.0 * (1 - value / total);
    const length = (iR + 2 * oR) / 4;
    const sin = Math.sin(-RADIAN * ang);
    const cos = Math.cos(-RADIAN * ang);
    const r = 5;
    const x0 = cx + 5;
    const y0 = cy + 5;
    const xba = x0 + r * sin;
    const yba = y0 - r * cos;
    const xbb = x0 - r * sin;
    const ybb = y0 + r * cos;
    const xp = x0 + length * cos;
    const yp = y0 + length * sin;
    const xtext = x0;
    const ytext = y0 + 20;

    return [
        <circle cx={x0} cy={y0} r={r} fill={color} stroke="none" />,
        <path d={`M${xba} ${yba}L${xbb} ${ybb} L${xp} ${yp} L${xba} ${yba}`} stroke="#none" fill={color} />,
        <text x={xtext} y={ytext} fill="black" textAnchor={'middle'} dominantBaseline="central" style={{ fontWeight: "bold" }}>
            {(currentValue ? new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(currentValue) : '') + ' ' + (unit || '')}
        </text>
    ];
};




export default function ChartV2Indicator(props) {
    const { chart, data, setData } = props;
    const [activelegend, setActivelegend] = useState([]);

    const [currentValue, setCurrentValue] = useState("");
    const [currentValueNeedle, setCurrentValueNeedle] = useState("");
    const [tendancyValue, setTendancyValue] = useState("");
    const [tendancyPercent, setTendancyPercent] = useState("");
    const [comparaisonValue, setComparaisonValue] = useState("");
    const [comparaisonPercent, setComparaisonPercent] = useState("");
    const [comparaisonText, setComparaisonText] = useState("");
    const [alert, setAlert] = React.useState(null);

    const [segmentdata, setSegmentdata] = useState([]);
    const targetRef = useRef();
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [cy, setCy] = useState(200);

    let movement_timer = null;
    const RESET_TIMEOUT = 100;

    const classes = useStyles();

    const test_dimensions = () => {
        if (targetRef.current) {
            setDimensions({
                width: targetRef.current.offsetWidth,
                height: targetRef.current.offsetHeight
            });
            //421
            let cytmp = 150;
            if (targetRef.current.offsetWidth > 350)
                cytmp = 175;
            else
                cytmp = targetRef.current.offsetWidth / 2;
            setCy(cytmp);
        }
    }

    useLayoutEffect(() => {
        test_dimensions();
    }, []);

    window.addEventListener('resize', () => {
        clearInterval(movement_timer);
        movement_timer = setTimeout(test_dimensions, RESET_TIMEOUT);
    });

    const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
        const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        return (
            <text x={x} y={y} fill="black" textAnchor={x > cx ? 'start' : 'end'} dominantBaseline="central" >
                {`${segmentdata[index].shortname}`}
            </text>
        );
    };

    useEffect(() => {

        // GetXLSXData(chart, datatmp, groupedseriestmp);

        let currentValuetmp = chart?.series?.find((serie) => serie.type === 'value')?.values[0]?.value;
        let tendancyValuetmp = chart?.series?.find((serie) => serie.type === 'tendancy')?.values[0]?.value;
        let comparaisonValuetmp = chart?.series?.find((serie) => serie.type === 'comparaison')?.values[0]?.value;
        let comparaisonTexttmp = chart?.series?.find((serie) => serie.type === 'comparaison')?.name;

        setCurrentValue(currentValuetmp);
        setTendancyValue(tendancyValuetmp);
        setComparaisonValue(comparaisonValuetmp);
        setComparaisonText(comparaisonTexttmp);

        if (tendancyValuetmp && currentValuetmp) {
            let tendancyPercenttmp = ((currentValuetmp / tendancyValuetmp) - 1) * 100;
            setTendancyPercent(tendancyPercenttmp);
        }
        if (comparaisonValuetmp && currentValuetmp) {
            let comparaisonPercenttmp = ((currentValuetmp / comparaisonValuetmp) - 1) * 100;
            setComparaisonPercent(comparaisonPercenttmp);
        }
        let gaugemin = chart?.indicatorparameters?.gauge?.min ? Number(chart.indicatorparameters.gauge.min) : 0;
        let gaugemax = chart?.indicatorparameters?.gauge?.max ? Number(chart.indicatorparameters.gauge.max) : 0;
        
        let currentValueNeedletmp = currentValuetmp;
        if (currentValuetmp > gaugemax)
            currentValueNeedletmp = gaugemax;
        else if (currentValuetmp < gaugemin)
            currentValueNeedletmp = gaugemin;
        currentValueNeedletmp = currentValueNeedletmp - gaugemin; //needle value is a relative position
        setCurrentValueNeedle(currentValueNeedletmp);

        let segmentdatatmp = [];
        chart.indicatorparameters.gauge.segments.forEach((segment) => {
            segmentdatatmp.push({ shortname: segment.shortname, name: segment.name, color: segment.color, value: (segment.max - segment.min) });
        });
        setSegmentdata(segmentdatatmp);
    }, []);


    const hideAlert = () => {
        setAlert(null);
    };
    const getTooltipHelpComparaison = () => {
        const tooltipAlert = (
            <SweetAlert
                style={{
                    fontSize: '14px',
                    fontFamily: '"Source Sans Pro",sans-serif',
                    color: ' #3c4858',
                    display: "block", marginTop: "-50px", width: "25rem"
                }}
                title=""
                showConfirm={false}
                onConfirm={() => hideAlert()}
                onCancel={() => hideAlert()}
            >
                <div style={{ fontSize: "14px" }}>
                    <p><b>{'Comparaison avec ' + comparaisonText}</b></p>
                    <HelpLine>{chart.indicatorparameters?.lowerisbetter ? "La valeur la plus basse est la meilleure" : "La valeur la plus haute est la meilleure"}</HelpLine>
                    <HelpLine>{"La valeur actuelle sur votre périmètre est de " + new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(currentValue) + chart.yaxis1unit}</HelpLine>
                    <HelpLine>{"La valeur sur " + comparaisonText + " est de " + new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(comparaisonValue) + chart.yaxis1unit}</HelpLine>
                </div>
            </SweetAlert>
        );
        setAlert(tooltipAlert);
    };
    const getTooltipHelpTendancy = () => {
        const tooltipAlert = (
            <SweetAlert
                style={{
                    fontSize: '14px',
                    fontFamily: '"Source Sans Pro",sans-serif',
                    color: ' #3c4858',
                    display: "block", marginTop: "-50px", width: "25rem"
                }}
                title=""
                showConfirm={false}
                onConfirm={() => hideAlert()}
                onCancel={() => hideAlert()}
            >
                <div style={{ fontSize: "14px" }}>
                    <p><b>{'Evolution'}</b></p>
                    <HelpLine>{chart.indicatorparameters?.lowerisbetter ? "La valeur la plus basse est la meilleure" : "La valeur la plus haute est la meilleure"}</HelpLine>
                    <HelpLine>{"La valeur actuelle sur votre périmètre est de " + new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(currentValue) + chart.yaxis1unit}</HelpLine>
                    <HelpLine>{"La valeur sur la période précédente est de " + new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(tendancyValue) + chart.yaxis1unit}</HelpLine>
                </div>
            </SweetAlert>
        );
        setAlert(tooltipAlert);
    };

    return (
        <>
            <div ref={targetRef}>
                <ResponsiveContainer width="100%" height={350} >
                    <PieChart
                        // width={500}
                        // height={300}
                        margin={{
                            top: 10,
                            right: 10,
                            left: 10,
                            bottom: 10,
                        }}
                    >
                        <defs>
                        {/* RED */}
                        <radialGradient id="colorRed" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#C53F3F" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#8E0018" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorRed2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#9D3232" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#710013" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorRed3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#762525" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#55000E" stopOpacity={0.9} />
                        </radialGradient>

                        {/* ORANGE */}
                        <radialGradient id="colorOrange" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#FF763A" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#C54608" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorOrange2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#CC5E2E" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#9D3806" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorOrange3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#994622" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#762A04" stopOpacity={0.9} />
                        </radialGradient>

                        {/* YELLOW */}
                        <radialGradient id="colorYellow" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#D6D22F" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#A1A100" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorYellow2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#ABA825" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#808000" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorYellow3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#807E1C" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#606000" stopOpacity={0.9} />
                        </radialGradient>

                        {/* GREEN */}
                        <radialGradient id="colorGreen" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#91C73C" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#5F9600" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorGreen2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#749F30" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#4C7800" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorGreen3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#577724" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#395A00" stopOpacity={0.9} />
                        </radialGradient>

                        {/* BLUE */}
                        <radialGradient id="colorBlue" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#3E8FC6" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#006295" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorBlue2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#31729E" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#004E77" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorBlue3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#255576" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#003A59" stopOpacity={0.9} />
                        </radialGradient>

                        {/* PURPLE */}
                        <radialGradient id="colorPurple" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#6B3CC8" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#340C96" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorPurple2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#5530A0" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#290978" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorPurple3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#402478" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#1F075A" stopOpacity={0.9} />
                        </radialGradient>

                        {/* GREY */}
                        <radialGradient id="colorGrey" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#9798A0" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#605F70" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorGrey2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#787980" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#4C4C59" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="colorGrey3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#5A5B60" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#393943" stopOpacity={0.9} />
                        </radialGradient>

                        {/* DPE A */}
                        <radialGradient id="dpeA" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#008A32" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#476E00" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeA2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#006E28" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#385800" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeA3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#00521E" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#2A4200" stopOpacity={0.9} />
                        </radialGradient>

                        {/* DPE B */}
                        <radialGradient id="dpeB" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#91C73C" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#5F9600" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeB2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#749F30" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#4C7800" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeB3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#577724" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#395A00" stopOpacity={0.9} />
                        </radialGradient>

                        {/* DPE C */}
                        <radialGradient id="dpeC" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#D6D22F" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#A1A100" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeC2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#ABA825" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#808000" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeC3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#807E1C" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#606000" stopOpacity={0.9} />
                        </radialGradient>

                        {/* DPE D */}
                        <radialGradient id="dpeD" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#FFFF66" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#B5B548" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeD2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#CCCC51" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#909039" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeD3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#99993D" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#6C6C2B" stopOpacity={0.9} />
                        </radialGradient>

                        {/* DPE E */}
                        <radialGradient id="dpeE" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#F8BB00" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#CE9A00" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeE2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#C69500" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#A47B00" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeE3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#947000" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#7B5C00" stopOpacity={0.9} />
                        </radialGradient>

                        {/* DPE F */}
                        <radialGradient id="dpeF" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#FF763A" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#C54608" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeF2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#CC5E2E" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#9D3806" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeF3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#994622" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#762A04" stopOpacity={0.9} />
                        </radialGradient>

                        {/* DPE G */}
                        <radialGradient id="dpeG" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#C53F3F" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#8E0018" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeG2" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#9D3232" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#710013" stopOpacity={0.9} />
                        </radialGradient>
                        <radialGradient id="dpeG3" x1="1" y1="1" x2="0" y2="0">
                            <stop offset="0%" stopColor="#762525" stopOpacity={0.9} />
                            <stop offset="100%" stopColor="#55000E" stopOpacity={0.9} />
                        </radialGradient>
                    </defs>
                        <Tooltip
                            cursor={{ fill: 'transparent' }}
                            offset={0}

                            content={<CustomTooltip chart={chart} />}
                        />
                        {/* <Legend
                    wrapperStyle={{ marginBottom: -40 }}
                    onClick={handleLegendClick}
                /> */}

                        <Pie
                            dataKey="value"
                            startAngle={180}
                            endAngle={0}
                            data={segmentdata}
                            // cx={cx}
                            cy={cy}
                            innerRadius="50%"
                            outerRadius="100%"
                            fill="#8884d8"
                            stroke="none"
                            labelLine={false}
                            label={renderCustomizedLabel}
                        >

                            {segmentdata.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                        </Pie>
                        {
                            needle(currentValueNeedle, segmentdata, dimensions ? (dimensions.width / 2) : 100, cy, cy, cy, 'url(#colorGrey)', chart.yaxis1unit, currentValue)
                        }
                        {/* {needle(value, segmentdata, cx, cy, iR, oR, 'url(#color7)')} */}
                    </PieChart>

                </ResponsiveContainer>
                <div style={{ marginTop: `${-(350 - cy - 50)}px` }}>
                    {
                        <DataContainer>
                            <Data>Tendance : </Data>
                            {((tendancyPercent > 0 && chart.indicatorparameters?.lowerisbetter) || (tendancyPercent < 0 && !chart.indicatorparameters?.lowerisbetter)) ?
                                <DataResultBad>{(tendancyPercent > 0 ? '+' : '') + (new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(tendancyPercent) + '%')}</DataResultBad> :
                                <DataResultGood>{(tendancyPercent > 0 ? '+' : '') + (new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(tendancyPercent) + '%')}</DataResultGood>
                            }
                            <HelpIcon className={classes.icon} onClick={() => getTooltipHelpTendancy()} />
                        </DataContainer>
                    }
                    {
                        comparaisonPercent ?
                            <DataContainer>
                                <Data>{'Comparaison avec ' + comparaisonText + ' : '}</Data>
                                {((comparaisonPercent > 0 && chart.indicatorparameters?.lowerisbetter) || (comparaisonPercent < 0 && !chart.indicatorparameters?.lowerisbetter)) ?
                                    <DataResultBad>{(comparaisonPercent > 0 ? '+' : '') + (new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(comparaisonPercent) + '%')}</DataResultBad> :
                                    <DataResultGood>{(comparaisonPercent > 0 ? '+' : '') + (new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 }).format(comparaisonPercent) + '%')}</DataResultGood>
                                }
                                <HelpIcon className={classes.icon} onClick={() => getTooltipHelpComparaison()} />
                            </DataContainer> : <></>
                    }

                </div>
            </div>
            {alert}
        </>
    );
}